import { SearchSuggestionProps } from 'front-commons/ds/components/Search/interfaces';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { UseSearchProps } from './interfaces';

export function useSearch({ filters, extrasFilters }: UseSearchProps) {
	const [searchValue, setSearchValue] = useState('');
	const [suggestions, setSuggestions] = useState<SearchSuggestionProps[] | undefined>();

	const filterOptions = useMemo(() => filters.flatMap((filter) => filter.options), [filters]);

	function handleSearchSuggestions(e: React.ChangeEvent<HTMLInputElement>) {
		setSearchValue(e.currentTarget.value);
	}

	function clearSearch() {
		setSearchValue('');
		handleClearSuggestions();
	}
	function handleClearSuggestions() {
		setSuggestions(undefined);
	}

	const getSuggestions = useCallback(() => {
		if (searchValue.length < 3) return;

		try {
			setSuggestions(getFilterOptions(searchValue));
		} catch (error) {
			setSuggestions(undefined);
		}
	}, [filters, searchValue]);

	function getFilterOption(value: string) {
		return filterOptions.find((option) => isSameOption(option.label, value));
	}

	function getFilterOptions(value: string) {
		return filterOptions.filter((option) => isSameOption(option.label, value));
	}

	function isSameOption(label: string, value: string) {
		return label.toLowerCase().includes(value.toLowerCase());
	}

	function getFilterOptionName(value: string) {
		return filters.find((filter) =>
			filter.options.find(
				(option) =>
					option.label.toLowerCase() === value.toLowerCase() || option.value.toLowerCase() === value.toLowerCase(),
			),
		)?.name;
	}

	function getExtraFilterOptionName(value: string) {
		return extrasFilters?.find(
			(option) =>
				option.label.toLowerCase() === value.toLowerCase() || option.value.toLowerCase() === value.toLowerCase(),
		)?.label;
	}

	useEffect(() => {
		getSuggestions();
	}, [filters, searchValue]);

	useEffect(() => {
		if (searchValue.length < 3) handleClearSuggestions();
	}, [searchValue]);

	return {
		searchValue,
		suggestions,
		clearSearch,
		getFilterOption,
		getFilterOptionName,
		handleClearSuggestions,
		handleSearchSuggestions,
		setSearchValue,
		getExtraFilterOptionName,
	};
}
